import React, { useEffect, useRef, useState } from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import PaginationList from '../../../Shared/Components/PaginationList'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import SectionBadge from '../SectionBadge'
import '../announcements.css'

const cleanFilters = (filters) => Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''))

const SectionsList = ({ sections, filters }) => {
    const { flash } = usePage().props
    const [name, setName] = useState(filters.name || '')
    const [toDelete, setToDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const firstRender = useRef(true)

    const visit = (next) =>
        Inertia.get(route('announcements.sections.index'), cleanFilters(next), {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        })

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        const timer = setTimeout(() => {
            if (name !== (filters.name || '')) visit({ ...filters, name })
        }, 400)
        return () => clearTimeout(timer)
    }, [name])

    const destroy = () => {
        setDeleting(true)
        Inertia.delete(route('announcements.sections.destroy', toDelete.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setToDelete(null)
            },
        })
    }

    const rows = sections.data || []

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/comunicados/secciones" />
            </span>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Header className="pt-7 flex-wrap gap-3">
                    <Card.Title className="flex-column align-items-start">
                        <span className="fw-bold text-gray-800">Catálogo de secciones</span>
                        <span className="text-muted fs-7 fw-semibold mt-1">Etiquetas de color para clasificar los comunicados</span>
                    </Card.Title>
                    <div className="card-toolbar">
                        <Link href={route('announcements.sections.create')} className="btn btn-sm btn-primary">
                            <i className="bi bi-plus-lg me-1"></i> Nueva sección
                        </Link>
                    </div>
                </Card.Header>
                <Card.Body className="pt-2">
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-6">
                        <Form.Control
                            type="search"
                            size="sm"
                            className="form-control-solid"
                            placeholder="Buscar por nombre…"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            style={{ maxWidth: 260 }}
                        />
                        <Form.Select
                            size="sm"
                            className="form-select-solid"
                            value={filters.status}
                            onChange={(e) => visit({ ...filters, status: e.target.value })}
                            style={{ maxWidth: 180 }}
                        >
                            <option value="">Todas</option>
                            <option value="active">Activas</option>
                            <option value="inactive">Inactivas</option>
                        </Form.Select>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
                            <thead>
                                <tr className="fw-bold text-muted text-uppercase fs-7 bg-light">
                                    <th className="min-w-200px ps-4 rounded-start">Sección</th>
                                    <th className="min-w-100px">Color</th>
                                    <th className="min-w-80px">Orden</th>
                                    <th className="min-w-100px">Comunicados</th>
                                    <th className="min-w-80px">Activa</th>
                                    <th className="min-w-100px text-end pe-4 rounded-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="fs-7">
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-10">
                                            Sin secciones.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.id}>
                                            <td className="ps-4">
                                                <SectionBadge section={row} className="fs-7" />
                                            </td>
                                            <td className="text-gray-700">
                                                <span className="d-inline-flex align-items-center gap-2">
                                                    <span
                                                        className="announcements-chip__dot"
                                                        style={{ backgroundColor: row.color, width: '1rem', height: '1rem' }}
                                                    ></span>
                                                    <code>{row.color}</code>
                                                </span>
                                            </td>
                                            <td className="text-gray-700">{row.sort_order}</td>
                                            <td className="text-gray-700">{row.announcements_count}</td>
                                            <td>
                                                {row.is_active ? (
                                                    <span className="badge badge-light-success">Sí</span>
                                                ) : (
                                                    <span className="badge badge-light-secondary">No</span>
                                                )}
                                            </td>
                                            <td className="text-end pe-4 text-nowrap">
                                                <Link
                                                    href={route('announcements.sections.edit', row.id)}
                                                    className="btn btn-sm btn-icon btn-light-primary me-2"
                                                    title="Editar"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Link>
                                                {row.announcements_count === 0 && (
                                                    <Button
                                                        size="sm"
                                                        variant="light-danger"
                                                        className="btn-icon"
                                                        title="Eliminar"
                                                        onClick={() => setToDelete(row)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="form-text mb-4">
                        Las secciones con comunicados no se pueden eliminar; desactívalas para ocultarlas al publicar.
                    </div>
                    <PaginationList links={sections.links} />
                </Card.Body>
            </Card>

            {toDelete && (
                <Modal show onHide={() => !deleting && setToDelete(null)} centered>
                    <Modal.Header closeButton={!deleting}>
                        <Modal.Title>Eliminar sección</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Eliminar la sección <strong>{toDelete.name}</strong>? Esta acción no se puede deshacer.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" disabled={deleting} onClick={() => setToDelete(null)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" disabled={deleting} onClick={destroy}>
                            {deleting ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Row>
    )
}

SectionsList.layout = (page) => <Layout title="Catálogo de secciones">{page}</Layout>

export default SectionsList
